/**
 * RTL Scanner — institutional options-flow imbalance scanner.
 *
 * React port of the standalone RTL-scanner frontend (public/index.html) from
 * github.com/wwwtraderwww/RTL-scanner. It renders the deduped contracts from the three
 * v9.1 imbalance lists as a heatmap or a table, and replaces the previous Elite Pulse
 * report view in the `elite-pulse` slot.
 *
 * The RTL-scanner Express backend is not needed here: `utils/rtlScannerTiles` performs the
 * exact same fetch → validate → normalize step against `/api/ois/report-pulse-summary-json`,
 * with a localStorage last-good cache standing in for `last-good.json`. The data-integrity
 * contract (no fabrication, only moneyness + colour derived, degrade to STALE/MAINTENANCE
 * instead of inventing data) is preserved — see the header of that module.
 *
 * Clicking a flow (tile or table row) opens that contract in the local historical modal via
 * HistoricalModalContext — the same drill-down the scanner table and the previous Elite Pulse
 * report view used, so contract history stays one shared component across the platform.
 *
 * The dataset is fixed per mount by the `source` prop, never toggled inside the view: the Elite
 * Pulse section renders imbalances only, and the Smart Flow section (see SmartFlowScannerView)
 * renders the flow engine's enriched tape only, with the field filters that tape supports.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './RtlScannerView.css';
import { ToolState } from './mmec/ToolState';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import AlertRulesPanel from './alerts/AlertRulesPanel';
import { useAlertRules } from '../hooks/useAlertRules';
import { ELITE_PULSE_SOURCE, flattenRawRecord, SMART_FLOW_SOURCE } from '../utils/alertSources';
import { ASSET_CLASS_OPTIONS, AssetClass } from '../utils/assetClass';
import { CAP_TIER_OPTIONS, CapTier } from '../utils/capTier';
import {
  isEmptyStoredFilters,
  matchesPulseBoardFilters,
  parseStoredPulseFilters,
  StoredPulseFilters,
} from '../utils/pulseBoardFilters';
import type { ContractHistoricalParams } from '../types/contractHistorical';
import { fetchContractBoard, fetchLiveTapeTiles, LIVE_TAPE_FIELDS } from '../utils/liveTapeTiles';
import { describeWindow, resolveTimeWindow, TIME_WINDOWS, TimeWindowId } from '../utils/timeWindows';
import {
  fetchScannerReport,
  tilesFromDailyBook,
  loadLastGood,
  saveLastGood,
  ScannerStatus,
  ScannerTile,
} from '../utils/rtlScannerTiles';
import {
  compareScannerTiles,
  parseFilterNumber,
  tileFlowClass as cls,
} from '../utils/rtlScannerSort';
import { verdictSummaryText } from '../utils/flowVerdictLabels';
import { fetchBookDates, fetchDailyBook } from '../services/pulseDailyBookService';
import {
  CustomFilter,
  defaultOpForType,
  FilterOp,
  FLOW_FIELD_BY_KEY,
  FLOW_FIELDS,
  matchesCustomFilters,
  OP_LABELS,
  opsForType,
} from '../utils/rtlScannerFlowRows';

const FAV_KEY = 'rtlScannerFavorites';
/** Toolbar filters, per dataset, so a reload or tab switch comes back to the same board. */
const FILTERS_KEY_PREFIX = 'rtlScannerFilters:';

function readStoredFilters(source: string): StoredPulseFilters {
  try {
    return parseStoredPulseFilters(window.localStorage.getItem(FILTERS_KEY_PREFIX + source));
  } catch {
    return parseStoredPulseFilters(null);
  }
}
const VIEW_KEY = 'rtlScannerView';
const COLS_KEY = 'rtlScannerColumns';
const COLS_VERSION_KEY = 'rtlScannerColumnsVersion';
/**
 * Bumped when columns are ADDED. A saved column set is a list of what to show, so a set
 * saved before a column existed hides it permanently -- the reader sees an empty board and
 * concludes the data is missing, when it is only unselected. On a version bump the columns
 * introduced since are added to the saved set; everything the reader chose is kept.
 */
const COLS_VERSION = 2;
const COLS_ADDED_SINCE_V1 = ['conv', 'flags', 'vdir', 'vstruct', 'vpos'];
const POLL_MS = 30000;

type ViewMode = 'heatmap' | 'table';
type SortDir = 1 | -1;

/**
 * Which dataset the scanner is showing: the pulse imbalance report, or the flow engine's
 * enriched tape ("Raw Flow"). Both render through the same tiles, filters and table.
 */
type ScannerSource = 'imbalances' | 'rawflow';

/** Ticker is always shown; the rest are toggleable. */
const DEFAULT_COLS = ['fl', 'strike', 'prem', 'conv', 'flags', 'vdir', 'vstruct', 'vpos', 'rv', 'exp', 'money', 'horizon'];

interface ColDef {
  key: string;
  label: string;
  sortKey: string;
  cell: (r: ScannerTile, c: string) => React.ReactNode;
}

function fmtPremium(p: number): string {
  if (p >= 1e6) return `$${(p / 1e6).toFixed(p >= 1e7 ? 0 : 1)}M`;
  if (p >= 1e3) return `$${Math.round(p / 1e3)}K`;
  return `$${p}`;
}

/** Colour = direction, mirroring the normalizer's `color` field. */

/**
 * Contract identity for the local historical view. Every field is taken straight off the
 * tile — the scanner never infers a contract from a symbol string, so a flow either opens
 * the exact contract the upstream reported or it does not open at all.
 */
function historicalParams(r: ScannerTile): ContractHistoricalParams {
  return {
    underlying: r.t,
    strike: r.strike,
    expiry: r.exp,
    optionType: r.side === 'C' ? 'CALL' : 'PUT',
    source: 'volume',
  };
}

const naSpan = (label: string) => <span className="na mono">{label}</span>;

const COLDEFS: ColDef[] = [
  { key: 't', label: 'Ticker', sortKey: 't', cell: (r) => r.t },
  { key: 'fl', label: 'Direction', sortKey: 'fl', cell: (r, c) => <span className={`tbadge ${c}`}>{r.fl}</span> },
  { key: 'strike', label: 'Strike', sortKey: 'strike', cell: (r) => <span className="mono">${r.strike}</span> },
  {
    key: 'prem',
    label: 'Premium',
    sortKey: 'prem',
    cell: (r) => (r.prem == null ? <span className="na">Not Available</span> : <span className="mono">{fmtPremium(r.prem)}</span>),
  },
  {
    key: 'rv',
    label: 'RelVol',
    sortKey: 'rv',
    cell: (r) => (r.rv == null ? <span className="na">Not Available</span> : <span className="mono">{r.rv.toFixed(2)}×</span>),
  },
  { key: 'exp', label: 'Expiry', sortKey: 'exp', cell: (r) => <span className="mono">{r.exp}</span> },
  {
    key: 'money',
    label: 'Moneyness',
    sortKey: 'money',
    cell: (r) => (r.money === '----' ? naSpan('----') : <span className="mono">{r.money}</span>),
  },
  { key: 'dte', label: 'DTE', sortKey: 'dte', cell: (r) => (r.dte == null ? <span className="na">—</span> : <span className="mono">{r.dte}d</span>) },
  { key: 'side', label: 'Side', sortKey: 'side', cell: (r) => (r.side === 'C' ? 'Call' : r.side === 'P' ? 'Put' : '—') },
  {
    key: 'horizon',
    label: 'Horizon',
    sortKey: 'horizon',
    cell: (r) => (r.horizon_tml ? <span className="mono">{r.horizon_tml}</span> : <span className="na">—</span>),
  },
  {
    key: 'conv',
    label: 'Conviction',
    sortKey: 'conv',
    // The book's own grading. It rides on every row and was being discarded, so the board
    // showed a premium with nothing to say how much the analysis believed it.
    cell: (r) => {
      if (!r.conviction) return <span className="na">—</span>;
      const strong = r.conviction === 'HIGH';
      return (
        <span
          className="mono"
          title={r.section ? `from ${r.section}` : undefined}
          style={{ fontWeight: strong ? 700 : 500 }}
        >
          {r.conviction}
        </span>
      );
    },
  },
  {
    key: 'flags',
    label: 'Flagged by',
    sortKey: 'flags',
    // How many separate analyses named this contract. A contract three blocks agree on is a
    // stronger signal than one flagged once, and that was invisible while duplicates were
    // discarded rather than merged.
    cell: (r) => {
      // The book counts how often the session surfaced a contract, which is the better
      // measure where it exists; the block count is the fallback for per-cycle rows.
      const n = r.appearances ?? r.sources?.length ?? 0;
      if (!n) return <span className="na">—</span>;
      const reinforced = r.repeatStatus === 'REINFORCED';
      return (
        <span
          className="mono"
          title={reinforced ? 'Strengthened during the session' : (r.sources || []).join(', ')}
          style={reinforced ? { color: 'var(--rtl-warn, #d68a00)', fontWeight: 700 } : undefined}
        >
          {n}×{reinforced && ' ↑'}
        </span>
      );
    },
  },
  {
    key: 'vdir',
    label: 'Bought / Sold',
    sortKey: 'vdir',
    // The share is shown beside the label because "Bought" at 56% and at 99% are different
    // claims, and a bare word hides which one this is.
    cell: (r) =>
      r.verdict?.direction == null ? (
        naSpan('Not Available')
      ) : (
        // Coloured by bias, like the tile (D1): bought call / sold put = up, bought put / sold call = down.
        <span
          className={`tbadge ${
            r.verdict.direction === 'Bought'
              ? r.side === 'P' ? 'sell' : 'buy'
              : r.verdict.direction === 'Sold'
                ? r.side === 'P' ? 'buy' : 'sell'
                : 'unk'
          }`}
        >
          {r.verdict.direction}
          {r.verdict.buyPct != null && (
            <span className="mono"> {Math.round(r.verdict.direction === 'Sold' ? 100 - r.verdict.buyPct : r.verdict.buyPct)}%</span>
          )}
        </span>
      ),
  },
  {
    key: 'vstruct',
    label: 'Legs',
    sortKey: 'vstruct',
    cell: (r) =>
      r.verdict?.structure == null ? (
        naSpan('Not Available')
      ) : (
        <span className="mono">
          {r.verdict.structure === 'Multi-leg' ? 'Multi' : r.verdict.structure === 'Single-leg' ? 'Single' : 'Mixed'}
          {r.verdict.multiPct != null && r.verdict.structure === 'Multi-leg' && (
            <span className="na"> {Math.round(r.verdict.multiPct)}%</span>
          )}
        </span>
      ),
  },
  {
    key: 'vpos',
    label: 'Open / Close',
    sortKey: 'vpos',
    // Preference order matters: the change in open interest states what happened, while the
    // volume ratio only suggests it. Whichever was used is shown, so the two are never
    // mistaken for each other — and the change carries the session it covers, because open
    // interest is stamped for the session that just ended, not the one trading now.
    cell: (r) => {
      const v = r.verdict;
      if (v?.position == null) return naSpan('Not Available');
      const cls =
        v.position === 'Opening' ? 'buy' : v.position === 'Closing' ? 'sell' : 'unk';
      return (
        <span
          className={`mono ${cls}`}
          title={
            v.oiChange != null
              ? `Open interest ${v.oiChange > 0 ? 'rose' : v.oiChange < 0 ? 'fell' : 'was unchanged'} by ${Math.abs(v.oiChange).toLocaleString()} over ${v.oiDate ?? 'the prior session'}`
              : 'From volume against open interest — suggestive, not conclusive'
          }
        >
          {v.position}
          {v.oiChange != null ? (
            <span className="na"> {v.oiChange > 0 ? '+' : ''}{v.oiChange.toLocaleString()} OI</span>
          ) : (
            v.volOi != null && (
              <span className="na"> {v.volOi < 0.01 ? v.volOi.toFixed(3) : v.volOi.toFixed(2)}×OI</span>
            )
          )}
        </span>
      );
    },
  },
];

/** Enriched-tape fields worth a table column of their own, in display order. */
const FLOW_COLUMN_KEYS = [
  'side', 'volume', 'size', 'oi', 'vol_oi_ratio', 'delta', 'gamma', 'theta', 'vega',
  'avg_iv', 'iv_rank', 'trade_style', 'spot', 'bid', 'ask', 'condition', 'trade_exchange',
  'market_cap', 'cap_tier', 'asset_class', 'timestamp',
];

function fmtFlowNumber(key: string, v: number): string {
  if (key === 'market_cap') return fmtPremium(v);
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toFixed(Math.abs(v) < 1 ? 4 : 2);
}

/** Raw Flow columns, generated from the field registry so the table covers every tape field. */
function makeFlowColdefs(keys: string[], labelOf: (k: string) => string): ColDef[] {
  return keys.map((key) => ({
    key: `raw_${key}`,
    label: labelOf(key),
    sortKey: `raw:${key}`,
    cell: (r: ScannerTile) => {
      const v = r.raw?.[key];
      if (v == null || v === '') return <span className="na">—</span>;
      if (typeof v === 'number') return <span className="mono">{fmtFlowNumber(key, v)}</span>;
      return <span className="mono">{String(v)}</span>;
    },
  }));
}

const LIVE_TAPE_COLUMN_KEYS = [
  'Side', 'Volume', 'Last', 'Stock', 'Spot', 'Condition', 'exchange', 'sequence', 'timestamp',
];

const FLOW_COLDEFS: ColDef[] = FLOW_COLUMN_KEYS.map((key) => ({
  key: `raw_${key}`,
  label: FLOW_FIELD_BY_KEY[key]?.label ?? key,
  sortKey: `raw:${key}`,
  cell: (r: ScannerTile) => {
    const v = r.raw?.[key];
    if (v == null || v === '') return <span className="na">—</span>;
    if (typeof v === 'number') return <span className="mono">{fmtFlowNumber(key, v)}</span>;
    return <span className="mono">{String(v)}</span>;
  },
}));

/** Keys offered by the toolbar dropdown; table headers can sort by more than these. */
const SORT_SELECT_KEYS = ['rv', 'strike', 'prem', 'exp', 't', 'fl', 'conv', 'flags', 'vdir', 'vstruct', 'vpos'];
/** Columns whose cells are numbers; header and cell align to the end so digits line up. */
const NUMERIC_COLS = new Set(['strike', 'prem', 'rv', 'dte']);


function readStoredSet(key: string, fallback: string[] | null): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback ? new Set(fallback) : null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback ? new Set(fallback) : null;
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return fallback ? new Set(fallback) : null;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — preferences simply do not persist.
  }
}

export interface RtlScannerViewProps {
  /** Tighter padding when embedded in the simple MMEC shell. */
  simpleLayout?: boolean;
  /**
   * Which dataset this mount shows. Fixed per section — Elite Pulse is imbalances only, Smart Flow
   * is the enriched tape only — so the two never mix inside one view.
   */
  source?: ScannerSource;
}

/**
 * One label/value line of a tile, omitted entirely when there is no value.
 *
 * A tile listing "Not Available" against half its rows reads as a broken tile, and the
 * reader has to pick the real findings out of the noise. Omitting the line is not the same
 * as inventing a value -- the data-integrity contract forbids filling a gap IN, not leaving
 * it out -- and the table view still renders every selected column, because there
 * alignment across rows is the whole point.
 */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === false) return null;
  return (
    <div className="r">
      <span className="k">{label}</span>
      {children}
    </div>
  );
}


const RtlScannerView: React.FC<RtlScannerViewProps> = ({
  simpleLayout = false,
  source = 'imbalances',
}) => {
  const isRawFlow = source === 'rawflow';
  // ── report state ────────────────────────────────────────────────────────────
  const [tiles, setTiles] = useState<ScannerTile[]>([]);
  /** Archived session to show, or '' for the live day the report carries. */
  const [bookDate, setBookDate] = useState('');
  const [bookDates, setBookDates] = useState<string[]>([]);
  const bookDateRef = useRef('');
  useEffect(() => {
    bookDateRef.current = bookDate;
  }, [bookDate]);
  useEffect(() => {
    if (isRawFlow) return undefined;
    let cancelled = false;
    fetchBookDates()
      .then((d) => {
        if (!cancelled) setBookDates(d);
      })
      // A missing archive list is not a broken board: the live day still loads.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isRawFlow]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [status, setStatus] = useState<ScannerStatus>('STALE');
  const tilesRef = useRef<ScannerTile[]>([]);
  tilesRef.current = tiles;
  /** How many prints match server-side; null when the source cannot report an exact count. */
  const [matchTotal, setMatchTotal] = useState<number | null>(null);
  // Live-tape filters travel with the request, so the loader reads them through refs.
  const searchRef = useRef('');
  const premRef = useRef(0);
  const dteRef = useRef(Infinity);
  const moneyRef = useRef('');
  const classRef = useRef('');
  const capRef = useRef('');
  const windowRef = useRef<TimeWindowId>('last_session');
  const boardRef = useRef<'contracts' | 'prints'>('contracts');

  /** Custom field filters — Smart Flow only, built from the enriched-tape field registry. */
  const [customFilters, setCustomFilters] = useState<CustomFilter[]>([]);
  const nextFilterId = useRef(0);

  // ── filters ─────────────────────────────────────────────────────────────────
  // Restored from the last visit, like the view and column choices beside them.
  const [storedFilters] = useState(() => readStoredFilters(source));
  const [search, setSearch] = useState(storedFilters.search);
  const [relInput, setRelInput] = useState(storedFilters.relInput);
  const [premInput, setPremInput] = useState(storedFilters.premInput);
  const [dteInput, setDteInput] = useState(storedFilters.dteInput);
  const [money, setMoney] = useState<Set<string>>(() => new Set(storedFilters.money));
  const [horizon, setHorizon] = useState<Set<string>>(() => new Set(storedFilters.horizon));
  /** Stock / ETF / index. These boards hold a few hundred rows, so classification is local. */
  const [assetClasses, setAssetClasses] = useState<Set<AssetClass>>(() => new Set(storedFilters.assetClasses));
  const [capTiers, setCapTiers] = useState<Set<CapTier>>(() => new Set(storedFilters.capTiers));
  useEffect(() => {
    const next: StoredPulseFilters = {
      search,
      relInput,
      premInput,
      dteInput,
      money: Array.from(money),
      horizon: Array.from(horizon),
      assetClasses: Array.from(assetClasses),
      capTiers: Array.from(capTiers),
    };
    try {
      if (isEmptyStoredFilters(next)) window.localStorage.removeItem(FILTERS_KEY_PREFIX + source);
      else window.localStorage.setItem(FILTERS_KEY_PREFIX + source, JSON.stringify(next));
    } catch {
      // Storage unavailable — filters simply do not persist.
    }
  }, [source, search, relInput, premInput, dteInput, money, horizon, assetClasses, capTiers]);
  /** Which slice of the tape to query. Recent days hold few symbols in great depth; earlier days
      hold thousands, so this changes the character of the board, not just its size. */
  const [timeWindow, setTimeWindow] = useState<TimeWindowId>('last_session');
  /** Contracts ranked by aggregate premium, or the individual prints behind them. */
  const [boardMode, setBoardMode] = useState<'contracts' | 'prints'>('contracts');
  /**
   * Which set of opportunities is on screen. Favorites get their own tab rather than a filter
   * chip so tracking a shortlist is a place you go, not a mode you have to remember you left on.
   */
  const [tab, setTab] = useState<'all' | 'favorites'>('all');

  // ── sort / view / columns / favorites ───────────────────────────────────────
  // The tape carries no relative volume, so Smart Flow opens sorted by premium instead.
  const [sortKey, setSortKey] = useState(isRawFlow ? 'prem' : 'rv');
  const [sortDir, setSortDir] = useState<SortDir>(-1);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const v = window.localStorage.getItem(VIEW_KEY);
      return v === 'table' || v === 'heatmap' ? v : 'heatmap';
    } catch {
      return 'heatmap';
    }
  });
  const [cols, setCols] = useState<Set<string>>(() => {
    const valid = new Set([...COLDEFS, ...FLOW_COLDEFS].map((c) => c.key));
    const stored = readStoredSet(COLS_KEY, null);
    if (!stored) return new Set(DEFAULT_COLS);
    let stamped = 1;
    try {
      stamped = Number(window.localStorage.getItem(COLS_VERSION_KEY)) || 1;
    } catch {
      stamped = 1;
    }
    if (stamped < COLS_VERSION) {
      for (const key of COLS_ADDED_SINCE_V1) stored.add(key);
      writeStored(COLS_KEY, Array.from(stored));
      try {
        window.localStorage.setItem(COLS_VERSION_KEY, String(COLS_VERSION));
      } catch {
        // Preference simply does not persist; the migration re-runs next load.
      }
    }
    return new Set(Array.from(stored).filter((k) => k !== 't' && valid.has(k)));
  });
  const [favs, setFavs] = useState<Set<string>>(() => readStoredSet(FAV_KEY, []) ?? new Set());
  const [colsOpen, setColsOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const colsMenuRef = useRef<HTMLDivElement | null>(null);
  const colsBtnRef = useRef<HTMLButtonElement | null>(null);

  // ── drill-down into the local contract history ──────────────────────────────
  const { openHistorical } = useHistoricalModal();

  /** Open one flow's contract in the local historical modal (same modal the scanner table uses). */
  const openContract = useCallback(
    (r: ScannerTile) => {
      openHistorical(historicalParams(r), { compactChrome: simpleLayout });
    },
    [openHistorical, simpleLayout],
  );

  /** Enter/Space activate a flow, matching the click behaviour for keyboard users. */
  const contractKeyDown = useCallback(
    (e: React.KeyboardEvent, r: ScannerTile) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openContract(r);
    },
    [openContract],
  );

  // ── data loading ────────────────────────────────────────────────────────────
  const loadTiles = useCallback(async () => {
    if (source === 'rawflow') {
      try {
        // The filters that QuestDB can answer go with the request; only the tape-field filters
        // below are applied to the page that comes back.
        const win = resolveTimeWindow(windowRef.current);
        const query = {
          search: searchRef.current,
          minPremium: premRef.current,
          maxDte: Number.isFinite(dteRef.current) ? dteRef.current : undefined,
          moneyness: moneyRef.current,
          underlyingClass: classRef.current,
          capTier: capRef.current,
          fromTs: win.fromTs,
          toTs: win.toTs,
        };
        if (boardRef.current === 'contracts') {
          // Aggregate premium per contract across the whole window — ranking single prints
          // returns hundreds of rows of one strike and a board of six.
          setTiles(await fetchContractBoard(query, 150));
          setMatchTotal(null);
        } else {
          const page = await fetchLiveTapeTiles(query);
          setTiles(page.tiles);
          setMatchTotal(page.totalExact ? page.total : null);
        }
        setAsOf(new Date().toISOString());
        setStatus('LIVE');
      } catch {
        // Same rule as the imbalance source: keep what last validated, never invent tape.
        setStatus(tilesRef.current.length ? 'STALE' : 'MAINTENANCE');
      }
      return;
    }
    try {
      // A picked session comes from the archive; with none picked the live report carries the
      // day's book itself. Both render through the same tiles, so the board is one board.
      if (bookDateRef.current) {
        const book = await fetchDailyBook(bookDateRef.current);
        const warns: string[] = [];
        setTiles(tilesFromDailyBook(book as unknown as Record<string, unknown>, warns));
        setAsOf(book?.session_date || bookDateRef.current);
        setStatus(book && book.opportunities.length ? 'LIVE' : 'MAINTENANCE');
        return;
      }
      const report = await fetchScannerReport();
      setTiles(report.tiles);
      setAsOf(report.report_generated_utc);
      setStatus('LIVE');
      saveLastGood({
        report_generated_utc: report.report_generated_utc,
        tiles: report.tiles,
        last_success_at: new Date().toISOString(),
      });
    } catch {
      // Never fabricate a replacement: keep serving the last validated report as STALE,
      // or declare MAINTENANCE when nothing has ever validated.
      setStatus(tilesRef.current.length ? 'STALE' : 'MAINTENANCE');
    }
  }, [source, bookDate]);

  useEffect(() => {
    // Switching source starts from an empty board so one feed never shows under the other's header.
    setTiles([]);
    setAsOf(null);
    setStatus('STALE');
    if (source === 'imbalances') {
      // Serve the on-device last-good copy immediately, before the first poll returns.
      const cached = loadLastGood();
      if (cached) {
        setTiles(cached.tiles);
        setAsOf(cached.report_generated_utc);
      }
    }
    loadTiles();
    const timer = window.setInterval(loadTiles, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadTiles, source]);

  // Close the columns menu on any outside click, as in the original.
  useEffect(() => {
    if (!colsOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (colsMenuRef.current?.contains(target) || colsBtnRef.current?.contains(target)) return;
      setColsOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [colsOpen]);

  // ── shared filter + sort (single source of truth for BOTH views) ────────────
  const relMin = parseFilterNumber(relInput) ?? 0;
  const premMin = parseFilterNumber(premInput) ?? 0;
  const dteMaxRaw = parseFilterNumber(dteInput);
  const dteMax = dteMaxRaw == null ? Infinity : dteMaxRaw;
  const searchLc = search.trim().toLowerCase();

  const list = useMemo(() => {
    const filtered = tiles.filter((r) => {
      // Live-tape rows arrive already filtered by the server, so re-applying the same chips
      // here would only narrow a page that has been narrowed once — and hide the true count.
      // Every toolbar filter, ANDed; see utils/pulseBoardFilters for each predicate.
      if (
        !isRawFlow &&
        !matchesPulseBoardFilters(r, {
          search: searchLc, relMin, premMin, dteMax, money, horizon, assetClasses, capTiers,
        })
      ) {
        return false;
      }
      if (isRawFlow && horizon.size && (!r.horizon_tml || !horizon.has(r.horizon_tml))) return false;
      if (tab === 'favorites' && !favs.has(r.id)) return false;
      if (!matchesCustomFilters(r.raw, customFilters)) return false;
      return true;
    });
    filtered.sort((a, b) => compareScannerTiles(a, b, sortKey, sortDir));
    return filtered;
  }, [tiles, searchLc, relMin, premMin, money, horizon, dteMax, assetClasses, capTiers, isRawFlow, tab, favs, customFilters, sortKey, sortDir]);

  // Server-side filters for the live tape: keep the refs current, and re-query when they move.
  const serverFilterKey = isRawFlow
    ? [searchLc, relInput, premInput, dteInput, Array.from(money).sort().join(','),
       Array.from(assetClasses).sort().join(','), Array.from(capTiers).sort().join(','),
       timeWindow, boardMode].join('|')
    : '';
  useEffect(() => {
    if (!isRawFlow) return undefined;
    searchRef.current = searchLc;
    premRef.current = premMin;
    dteRef.current = dteMax;
    // The tape takes one moneyness and one class at a time; a multi-select falls back to
    // whichever was picked first rather than silently ANDing into an empty board.
    moneyRef.current = Array.from(money)[0] ?? '';
    classRef.current = Array.from(assetClasses)[0] ?? '';
    capRef.current = Array.from(capTiers)[0] ?? '';
    windowRef.current = timeWindow;
    boardRef.current = boardMode;
    const t = window.setTimeout(() => loadTiles(), 350);
    return () => window.clearTimeout(t);
  }, [serverFilterKey, isRawFlow]);


  // Alerts watch what the board is actually showing, so a rule respects the filters above it.
  /** Custom filters and extra columns describe whichever tape this mount is showing. */
  const fieldRegistry = isRawFlow ? LIVE_TAPE_FIELDS : FLOW_FIELDS;
  const fieldByKey = useMemo(
    () => fieldRegistry.reduce((acc, f) => { acc[f.key] = f; return acc; }, {} as Record<string, typeof fieldRegistry[number]>),
    [fieldRegistry],
  );
  const alertSource = isRawFlow ? SMART_FLOW_SOURCE : ELITE_PULSE_SOURCE;
  const alertRecords = useMemo(
    () => (isRawFlow ? list.map((r) => flattenRawRecord(r as unknown as Record<string, unknown>)) : (list as unknown as Record<string, unknown>[])),
    [list, isRawFlow],
  );
  useAlertRules(alertSource, alertRecords, { paused: status === 'MAINTENANCE' });

  // ── handlers ────────────────────────────────────────────────────────────────
  const toggleFav = (id: string) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeStored(FAV_KEY, Array.from(next));
      return next;
    });
  };

  const toggleInSet = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string,
  ) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleCol = (key: string) => {
    setCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeStored(COLS_KEY, Array.from(next));
      try {
        window.localStorage.setItem(COLS_VERSION_KEY, String(COLS_VERSION));
      } catch {
        // Preference simply does not persist.
      }
      return next;
    });
  };

  const changeView = (next: ViewMode) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Preference simply does not persist.
    }
    if (next !== 'table') setColsOpen(false);
  };

  const addCustomFilter = () => {
    const def = fieldRegistry.find((f) => f.key === 'Premium') ?? fieldRegistry[0];
    nextFilterId.current += 1;
    setCustomFilters((prev) => [
      ...prev,
      { id: `f${nextFilterId.current}`, field: def.key, op: defaultOpForType(def.type), value: '' },
    ]);
  };

  const updateCustomFilter = (id: string, patch: Partial<CustomFilter>) => {
    setCustomFilters((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const next = { ...f, ...patch };
        if (patch.field && patch.field !== f.field) {
          // A new field may not support the old operator, and its old value rarely transfers.
          next.op = defaultOpForType(fieldByKey[patch.field]?.type ?? 'text');
          next.value = '';
        }
        return next;
      }),
    );
  };

  const removeCustomFilter = (id: string) => {
    setCustomFilters((prev) => prev.filter((f) => f.id !== id));
  };

  /** Header click: same key flips direction, new key starts descending. */
  const sortByKey = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === -1 ? 1 : -1));
    else {
      setSortKey(key);
      setSortDir(-1);
    }
  };

  /** Preset chips also fill the matching numeric input, and toggle off when re-clicked. */
  const chipClick = (current: string, value: string, setter: (v: string) => void) => {
    setter(current === value ? '' : value);
  };

  const resetAll = () => {
    setSearch('');
    setRelInput('');
    setPremInput('');
    setDteInput('');
    setMoney(new Set());
    setHorizon(new Set());
    setAssetClasses(new Set());
    setCapTiers(new Set());
    setTimeWindow('last_session');
    setBoardMode('contracts');
    setTab('all');
    setSortKey(isRawFlow ? 'prem' : 'rv');
    setSortDir(-1);
    setCustomFilters([]);
    setCols(new Set(DEFAULT_COLS));
    try {
      window.localStorage.removeItem(FILTERS_KEY_PREFIX + source);
      window.localStorage.removeItem(COLS_KEY);
      window.localStorage.removeItem(COLS_VERSION_KEY);
    } catch {
      // Nothing to clear.
    }
  };

  // ── render helpers ──────────────────────────────────────────────────────────
  const isTable = view === 'table';
  const isMaint = status === 'MAINTENANCE';
  // Raw Flow adds one column per enriched-tape field; both sets go through the same picker.
  const availableCols = isRawFlow
    ? [...COLDEFS, ...makeFlowColdefs(LIVE_TAPE_COLUMN_KEYS, (k) => fieldByKey[k]?.label ?? k)]
    : COLDEFS;
  const activeCols = availableCols.filter((c) => c.key === 't' || cols.has(c.key));
  const asOfLabel = asOf ? `${asOf.replace('T', ' ').slice(0, 16)} UTC` : '—';

  /** Empty message: the tool's own words; under the Design System preview, the system's empty state. */
  const emptyBlock = (text: string) =>
    isDesignSystemOn() ? <ToolState kind="empty" title={text} body="" /> : <div className="empty">{text}</div>;

  const renderHeatmap = () => {
    if (!tiles.length) return emptyBlock('No report data available.');
    if (!list.length) {
      if (tab === 'favorites' && !favs.size) {
        return emptyBlock('Nothing starred yet — click ★ on an opportunity to track it here.');
      }
      return emptyBlock('No tiles match these filters.');
    }
    return (
      <div className="grid">
        {list.map((r) => {
          const c = cls(r);
          const on = favs.has(r.id);
          return (
            <div
              className={`tile ${c}`}
              key={r.id}
              role="button"
              tabIndex={0}
              title={`${r.t} $${r.strike} ${r.exp} — ${r.verdict ? verdictSummaryText(r.verdict) : 'no tape reading'} — open contract history`}
              onClick={() => openContract(r)}
              onKeyDown={(e) => contractKeyDown(e, r)}
            >
              <div className="top">
                <span className="tkbox">{r.t}</span>
                <button
                  type="button"
                  className={`star${on ? ' on' : ''}`}
                  aria-label="favorite"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFav(r.id);
                  }}
                >
                  {on ? '★' : '☆'}
                </button>
                <span className={`flag ${c}`}>{r.fl}</span>
              </div>
              <div className="rows">
                <div className="r">
                  <span className="k">Strike</span>
                  <span className="v mono">${r.strike}</span>
                </div>
                <DetailRow label="Premium">
                  {r.prem == null ? null : <span className="v mono">{fmtPremium(r.prem)}</span>}
                </DetailRow>
                <DetailRow label="RelVol">
                  {r.rv == null ? null : <span className="v mono">{r.rv.toFixed(2)}×</span>}
                </DetailRow>
                <div className="r">
                  <span className="k">Expiry</span>
                  <span className="v mono">{r.exp}</span>
                </div>
                <DetailRow label="Moneyness">
                  {r.money === '----' ? null : <span className="v mono">{r.money}</span>}
                </DetailRow>
                <DetailRow label="Horizon">
                  {r.horizon_tml ? <span className="v mono">{r.horizon_tml}</span> : null}
                </DetailRow>
                <DetailRow label="Conviction">
                  {r.conviction ? (
                    <span className="v mono" style={{ fontWeight: r.conviction === 'HIGH' ? 700 : 500 }}>
                      {r.conviction}
                      {r.appearances && r.appearances > 1 ? ` · ${r.appearances}×` : ''}
                      {r.repeatStatus === 'REINFORCED' ? ' ↑' : ''}
                    </span>
                  ) : null}
                </DetailRow>
                <DetailRow label="Bought / Sold">
                  {r.verdict?.direction == null ? null : (
                    <span className="v mono">
                      {r.verdict.direction}
                      {r.verdict.buyPct != null &&
                        ` ${Math.round(r.verdict.direction === 'Sold' ? 100 - r.verdict.buyPct : r.verdict.buyPct)}%`}
                    </span>
                  )}
                </DetailRow>
                <DetailRow label="Legs">
                  {r.verdict?.structure == null ? null : (
                    <span className="v mono">{r.verdict.structure}</span>
                  )}
                </DetailRow>
                <DetailRow label="Open / Close">
                  {r.verdict?.position == null ? null : (
                    <span className="v mono">{r.verdict.position}</span>
                  )}
                </DetailRow>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTable = () => (
    <div className="tablewrap">
      <table className="tbl">
        <thead>
          <tr>
            <th className="th-star" aria-label="favorite" />
            {activeCols.map((c) => {
              const sorted = c.sortKey === sortKey;
              const arrow = sorted ? (sortDir === -1 ? '↓' : '↑') : '⇅';
              return (
                <th
                  key={c.key}
                  className={`sortable${sorted ? ' sorted' : ''}${NUMERIC_COLS.has(c.key) ? ' num' : ''}`}
                  onClick={() => sortByKey(c.sortKey)}
                >
                  {c.label}
                  <span className="sarrow">{arrow}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {!tiles.length || !list.length ? (
            <tr>
              <td colSpan={activeCols.length + 1}>
                {emptyBlock(
                  !tiles.length
                    ? 'No report data available.'
                    : tab === 'favorites' && !favs.size
                      ? 'Nothing starred yet — click ★ on a row to track it here.'
                      : 'No rows match these filters.',
                )}
              </td>
            </tr>
          ) : (
            list.map((r) => {
              const c = cls(r);
              const on = favs.has(r.id);
              return (
                <tr
                  className={c}
                  key={r.id}
                  tabIndex={0}
                  title={`${r.t} $${r.strike} ${r.exp} — open contract history`}
                  onClick={() => openContract(r)}
                  onKeyDown={(e) => contractKeyDown(e, r)}
                >
                  <td className="th-star">
                    <button
                      type="button"
                      className="tstar"
                      aria-label="favorite"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFav(r.id);
                      }}
                    >
                      {on ? '★' : '☆'}
                    </button>
                  </td>
                  {activeCols.map((col) => (
                    <td className={`c-${col.key}${NUMERIC_COLS.has(col.key) ? ' num' : ''}`} key={col.key}>
                      {col.cell(r, c)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const numChip = (
    label: string,
    value: string,
    current: string,
    setter: (v: string) => void,
  ) => (
    <button
      type="button"
      className={`chip${current === value ? ' on' : ''}`}
      onClick={() => chipClick(current, value, setter)}
    >
      {label}
    </button>
  );

  const setChip = (label: string, value: string, active: Set<string>, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (
    <button
      type="button"
      className={`chip${active.has(value) ? ' on' : ''}`}
      onClick={() => toggleInSet(setter, value)}
    >
      {label}
    </button>
  );

  return (
    <div className={`rtlscan${simpleLayout ? ' simple' : ''}`}>
      <div className="head">
        <div>
          <h1>
            {isRawFlow ? 'Top Premium Board' : 'RTL Scanner'}{' '}
            <span>· {isRawFlow ? 'Live Tape' : 'Imbalances'}</span>
          </h1>
          {isRawFlow && (
            <p className="rtlscan-sub" style={{ margin: '2px 0 0', fontSize: 12, opacity: 0.7 }}>Biggest money in options right now, refreshed every 30 seconds</p>
          )}
        </div>
        <div className="hr">
          <div className="vtoggle">
            <button type="button" className={tab === 'all' ? 'on' : ''} onClick={() => setTab('all')}>
              All
            </button>
            <button
              type="button"
              className={tab === 'favorites' ? 'on' : ''}
              onClick={() => setTab('favorites')}
              title="Opportunities you have starred, saved in this browser"
            >
              ★ Favorites{favs.size ? ` (${favs.size})` : ''}
            </button>
          </div>
          <div className="vtoggle">
            <button type="button" className={isTable ? '' : 'on'} onClick={() => changeView('heatmap')}>
              Heatmap
            </button>
            <button type="button" className={isTable ? 'on' : ''} onClick={() => changeView('table')}>
              Table
            </button>
          </div>
          {isTable && (
            <div className="colswrap">
              <button
                type="button"
                className="chip colsbtn"
                ref={colsBtnRef}
                onClick={() => setColsOpen((o) => !o)}
              >
                ☰ Columns
              </button>
              <div className={`colsmenu${colsOpen ? ' show' : ''}`} ref={colsMenuRef}>
                <div className="hd">Columns</div>
                <label className="locked">
                  <input type="checkbox" checked disabled readOnly />
                  Ticker
                </label>
                {availableCols.filter((c) => c.key !== 't').map((c) => (
                  <label key={c.key}>
                    <input type="checkbox" checked={cols.has(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            className={`chip${alertsOpen ? ' on' : ''}`}
            onClick={() => setAlertsOpen((v) => !v)}
            title="Alert rules for this feed"
          >
            🔔 Alerts
          </button>
          <span className={`livebadge ${status === 'LIVE' ? 'live' : 'stale'}`}>
            <span className="dot" />
            <span>{status === 'LIVE' ? 'Live' : 'Stale'}</span>
          </span>
          {isRawFlow && <span className="status">{describeWindow(timeWindow)}</span>}
          <span className="status">
            Report as of <b>{asOfLabel}</b>
          </span>
        </div>
      </div>

      {isMaint && isDesignSystemOn() ? (
        <ToolState
          kind="error"
          title="Under maintenance — back shortly"
          body="Live data is temporarily unavailable. The scanner refreshes automatically once it's restored."
        />
      ) : isMaint ? (
        <div className="maint">
          <div className="mdot" />
          <h2>Under maintenance — back shortly</h2>
          <p>Live data is temporarily unavailable. The scanner refreshes automatically once it&apos;s restored.</p>
        </div>
      ) : (
        <>
          <div className="bar">
            <div className="fg">
              <span className="lab">Search</span>
              <div className="ctl">
                <input
                  className="num mono search"
                  type="text"
                  placeholder="ticker…"
                  autoComplete="off"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            {!isTable && (
              <div className="fg">
                {!isRawFlow && bookDates.length > 0 && (
                  <>
                    <span className="lab">Session</span>
                    <select
                      className="sel"
                      value={bookDate}
                      onChange={(e) => setBookDate(e.target.value)}
                      title="Today is the live book the report carries; earlier dates come from the archive."
                    >
                      <option value="">Today (live)</option>
                      {bookDates.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <span className="lab">Sort by</span>
                <div className="ctl">
                  <select
                    className="sel"
                    value={SORT_SELECT_KEYS.includes(sortKey) ? sortKey : ''}
                    onChange={(e) => setSortKey(e.target.value)}
                  >
                    <option value="" hidden />
                    {!isRawFlow && <option value="rv">RelVol</option>}
                    <option value="strike">Strike</option>
                    <option value="prem">Premium</option>
                    <option value="exp">Expiry</option>
                    <option value="t">Ticker</option>
                    <option value="fl">Direction</option>
                    <option value="conv">Conviction</option>
                    <option value="flags">Flagged by</option>
                    <option value="vdir">Bought / Sold</option>
                    <option value="vstruct">Legs</option>
                    <option value="vpos">Open / Close</option>
                  </select>
                  <button
                    type="button"
                    className="dirbtn"
                    title="toggle direction"
                    onClick={() => setSortDir((d) => (d === -1 ? 1 : -1))}
                  >
                    {sortDir === -1 ? '↓' : '↑'}
                  </button>
                </div>
              </div>
            )}
            {isRawFlow && (
              <div className="fg">
                <span className="lab">Session</span>
                <div className="ctl">
                  {TIME_WINDOWS.map((w) => (
                    <button
                      key={w.id}
                      type="button"
                      className={`chip${timeWindow === w.id ? ' on' : ''}`}
                      title={describeWindow(w.id)}
                      onClick={() => setTimeWindow(w.id)}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isRawFlow && (
              <div className="fg">
                <span className="lab">Rank</span>
                <div className="ctl">
                  <button
                    type="button"
                    className={`chip${boardMode === 'contracts' ? ' on' : ''}`}
                    title="Contracts ranked by total premium across every matching print"
                    onClick={() => setBoardMode('contracts')}
                  >
                    Contracts
                  </button>
                  <button
                    type="button"
                    className={`chip${boardMode === 'prints' ? ' on' : ''}`}
                    title="The individual prints, largest first"
                    onClick={() => setBoardMode('prints')}
                  >
                    Prints
                  </button>
                </div>
              </div>
            )}
            {/* Relative volume and horizon exist only on the imbalance report, not on the tape. */}
            {!isRawFlow && (
              <div className="fg">
                <span className="lab">RelVol ≥</span>
                <div className="ctl">
                  {numChip('5×', '5', relInput, setRelInput)}
                  {numChip('10×', '10', relInput, setRelInput)}
                  {numChip('15×', '15', relInput, setRelInput)}
                  <input
                    className="num mono"
                    type="number"
                    min={0}
                    placeholder="x…"
                    value={relInput}
                    onChange={(e) => setRelInput(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className="fg">
              <span className="lab">Premium ≥</span>
              <div className="ctl">
                {numChip('$1M', '1000000', premInput, setPremInput)}
                {numChip('$5M', '5000000', premInput, setPremInput)}
                {numChip('$10M', '10000000', premInput, setPremInput)}
                <input
                  className="num mono"
                  type="number"
                  min={0}
                  placeholder="$…"
                  value={premInput}
                  onChange={(e) => setPremInput(e.target.value)}
                />
              </div>
            </div>
            <div className="fg">
              <span className="lab">Underlying</span>
              <div className="ctl">
                {ASSET_CLASS_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`chip${assetClasses.has(o.value) ? ' on' : ''}`}
                    onClick={() =>
                      setAssetClasses((prev) => {
                        const next = new Set(prev);
                        if (next.has(o.value)) next.delete(o.value);
                        else next.add(o.value);
                        return next;
                      })
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="fg">
              <span className="lab">Cap tier</span>
              <div className="ctl">
                {CAP_TIER_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`chip${capTiers.has(o.value) ? ' on' : ''}`}
                    onClick={() =>
                      setCapTiers((prev) => {
                        const next = new Set(prev);
                        if (next.has(o.value)) next.delete(o.value);
                        else next.add(o.value);
                        return next;
                      })
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="fg">
              <span className="lab">Moneyness</span>
              <div className="ctl">
                {setChip('OTM', 'OTM', money, setMoney)}
                {setChip('ATM', 'ATM', money, setMoney)}
                {setChip('ITM', 'ITM', money, setMoney)}
              </div>
            </div>
            <div className="fg">
              <span className="lab">Expiry ≤ (days)</span>
              <div className="ctl">
                {numChip('30d', '30', dteInput, setDteInput)}
                {numChip('90d', '90', dteInput, setDteInput)}
                {numChip('180d', '180', dteInput, setDteInput)}
                <input
                  className="num mono"
                  type="number"
                  min={0}
                  placeholder="days…"
                  value={dteInput}
                  onChange={(e) => setDteInput(e.target.value)}
                />
              </div>
            </div>
            {!isRawFlow && (
              <div className="fg">
                <span className="lab">Horizon</span>
                <div className="ctl">
                  {setChip('Long', '[L]', horizon, setHorizon)}
                  {setChip('Mid', '[M]', horizon, setHorizon)}
                  {setChip('Short', '[T]', horizon, setHorizon)}
                </div>
              </div>
            )}
            <button type="button" className="reset" onClick={resetAll}>
              Reset
            </button>
          </div>

          {isRawFlow && (
            <div className="cfbar">
              <div className="cfhead">
                <span className="lab">Field filters</span>
                <button type="button" className="chip" onClick={addCustomFilter}>
                  + Add filter
                </button>
                {customFilters.length > 0 && (
                  <button type="button" className="chip" onClick={() => setCustomFilters([])}>
                    Clear
                  </button>
                )}
                <span className="cfhint">
                  Any of the {fieldRegistry.length} tape fields — premium, greeks, IV rank, vol/OI, trade style,
                  exchange. All conditions must match.
                </span>
              </div>
              {customFilters.length > 0 && (
                <div className="cflist">
                  {customFilters.map((f) => {
                    const def = fieldByKey[f.field];
                    const type = def?.type ?? 'text';
                    return (
                      <div className="cfrow" key={f.id}>
                        <select
                          className="sel"
                          value={f.field}
                          onChange={(e) => updateCustomFilter(f.id, { field: e.target.value })}
                        >
                          {fieldRegistry.map((d) => (
                            <option key={d.key} value={d.key}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="sel"
                          value={f.op}
                          onChange={(e) => updateCustomFilter(f.id, { op: e.target.value as FilterOp })}
                        >
                          {opsForType(type).map((op) => (
                            <option key={op} value={op}>
                              {OP_LABELS[op]}
                            </option>
                          ))}
                        </select>
                        {type === 'enum' ? (
                          <select
                            className="sel"
                            value={f.value}
                            onChange={(e) => updateCustomFilter(f.id, { value: e.target.value })}
                          >
                            <option value="">any</option>
                            {(def?.options ?? []).map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="num mono"
                            type={type === 'number' ? 'number' : 'text'}
                            placeholder={type === 'number' ? 'value…' : 'text…'}
                            value={f.value}
                            onChange={(e) => updateCustomFilter(f.id, { value: e.target.value })}
                          />
                        )}
                        <button
                          type="button"
                          className="cfdel"
                          aria-label="remove filter"
                          onClick={() => removeCustomFilter(f.id)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="count">
            showing <b>{list.length}</b> of{' '}
            <b>
              {tab === 'favorites'
                ? favs.size
                : isRawFlow && matchTotal != null
                  ? matchTotal.toLocaleString()
                  : tiles.length}
            </b>
            {isRawFlow && matchTotal != null && tab !== 'favorites' ? ' matching prints' : ''}
            {tab === 'favorites' ? ' favorited' : ''} · <b>{favs.size}</b> favorited
          </div>

          {isTable ? renderTable() : renderHeatmap()}

          <div className="legend">
            <div className="it">
              <span className="sw" style={{ background: 'var(--up)' }} /> Bullish (Buy Call / Sell Put)
            </div>
            <div className="it">
              <span className="sw" style={{ background: 'var(--down)' }} /> Bearish (Buy Put / Sell Call)
            </div>
            <div className="it">
              <span className="sw" style={{ background: 'var(--border)' }} /> Unknown
            </div>
            <div className="it">
              <span style={{ color: 'var(--rtl-star)' }}>★</span> favorite (saved in your browser)
            </div>
          </div>
          <div className="note">
            Color = <b>direction</b>: Buy Call / Sell Put = bullish (green), Buy Put / Sell Call = bearish (red).
            Moneyness is <b>----</b> when the spot source is unreliable for that ticker.{' '}
            {isRawFlow ? (
              <>
                Smart Flow reads the live tape, ranked by premium: newest-first returns one
                saturating symbol inside a single second. Premium, expiry, moneyness, underlying and
                cap filters run in QuestDB across every matching print; relative volume and horizon
                are not on the tape, so they read <b>Not Available</b>.{' '}
              </>
            ) : (
              <>
                <b>Horizon</b> = trade time-frame: <b>[L]</b> long, <b>[M]</b> mid, <b>[T]</b> short.{' '}
              </>
            )}
            Click any flow to open its <b>contract history</b>.
          </div>
        </>
      )}

      {alertsOpen && (
        <div className="mmecAlertDrawer">
          <AlertRulesPanel source={alertSource} onClose={() => setAlertsOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default RtlScannerView;
